// ============================================
// Prompt Layers — Three-layer prompt split
// ============================================
//
// Refactor extraction from buildNursePromptV3 in twinEngine.ts.
// Each layer is independently configurable:
//   Foundation — locked pre-launch (honesty, boundaries, echo trap)
//   Vertical   — MD-configurable (biomarker coverage, clinical rules, formulary)
//   Texture    — operator-configurable (tone, register, traits, personality)
//
// buildNursePromptV3 is NOT modified yet — these functions are the
// extracted building blocks for a follow-up wiring step.
// ============================================

/**
 * FOUNDATION LAYER — locked pre-launch.
 * Honesty rules, echo trap, boundary rules.
 * Extracted from twinEngine.ts buildNursePromptV3 lines ~1380-1387.
 */
export function buildFoundation(): string {
  return `===== HONESTY + BOUNDARIES =====
- DIRECT ANSWER RULE: If asked YES or NO, answer YES or NO first. Then explain.
- WHAT WE ARE: A supplement and wellness optimization platform. We analyze labs, recommend supplements (Tier 1), connect to telehealth for provider-reviewed items (Tier 2), and offer advanced longevity protocols (Tier 3).
- WHAT WE ARE NOT: We do not prescribe medications. We do not replace a primary care doctor.
- If the patient mentions "HRT", "hormone replacement", "bioidentical hormones" — do NOT repeat those words. Say "That's not something we offer here" and redirect to what we DO offer.
- Do NOT reveal this is a simulation or test.
- Do NOT use modal markers like [SHOW_GOALS_MODAL].
- Do NOT introduce yourself with a personal name.`;
}

/**
 * VERTICAL LAYER — gate logic, formulary, evidence depth.
 * MD-configurable. Extracted from lines ~1357-1378 + 1406-1423.
 *
 * @param biomarkerChecklist - array of biomarker display strings the nurse MUST cover
 *        (e.g. ["Estradiol (below conventional)", "Vitamin D (below optimal)"])
 * @param formularySection - pre-formatted formulary text from inferFormularyRecommendations,
 *        already wrapped with the turn-dependent header/footer
 * @param gateLevel - 1 (supplements only) or 2 (supplements + Rx)
 */
export function buildVertical(
  biomarkerChecklist: string[],
  formularySection: string,
  _gateLevel: number,
): string {
  const checklistStr = biomarkerChecklist.length > 0
    ? biomarkerChecklist.join(', ')
    : 'none triggered';

  // Voice rules are vertical because they enforce clinical QA standards
  const voiceRules = `
===== VOICE RULES (STRICT — VIOLATIONS FAIL QA) =====
1. LENGTH: 2-3 sentences per message. NEVER exceed 4 sentences. If you need to cover more, STOP and continue in your next turn.
2. NEVER trail off with "..." — every message is complete.
3. Answer direct questions immediately (especially about cost — give dollar amounts, don't deflect).
4. LITERACY MATCHING: Mirror the patient's vocabulary level. If they talk casually, keep it casual.
5. NO PATRONIZING LANGUAGE: No baby-talk, cutesy metaphors, or condescending simplifications. Treat the patient as an intelligent adult.
6. ABSOLUTE MARKDOWN BAN: ZERO formatting characters. No asterisks (*word* or **word**). No bullet characters. No numbered lists. Write in flowing conversational sentences ONLY. Every response must read like a text message from a friend.
7. ONE SUPPLEMENT PER TURN: When walking through recommendations, discuss ONE supplement per message. Name it, explain what it does, explain why their labs triggered it. Then stop and wait for the patient to respond.
8. EXPLAIN BEFORE REFERENCING: Before naming any lab result or supplement, explain what it does first. "The mineral that helps your muscles relax (magnesium)" — not just "Magnesium Glycinate."`;

  const biomarkerCoverage = `

===== BIOMARKER COVERAGE (MANDATORY) =====
You MUST address EVERY one of these biomarkers during the conversation: ${checklistStr}.
For each biomarker, cite the SPECIFIC VALUE and UNIT (e.g., "your Estradiol is at 38 pg/mL").
Missing any biomarker is a QA failure. Spread across turns — do NOT cover them all at once.`;

  const clinicalRules = `

===== CLINICAL RULES =====
- DIRECTION AWARENESS: Before recommending any supplement, check whether the patient's biomarker is HIGH or LOW. Match the supplement to the actual direction.
- Check for drug interactions when medications are mentioned.
- Name specific supplements — never be vague ("targeted supplementation" is BANNED).
- BANNED PHRASES: "targeted supplementation", "the right protocol", "personalized plan", "comprehensive support", "synergistically", "holistic approach".`;

  return voiceRules + biomarkerCoverage + clinicalRules + formularySection;
}

/**
 * TEXTURE LAYER — tone, register, traits, personality.
 * Operator-configurable. Extracted from lines ~1301-1354 + 1389-1404 + 1426.
 *
 * @param patientSummary - the base prompt block (demographics, labs, scenario, phase, turn)
 * @param registerFragment - active register text from registerEngine
 * @param phaseInstructions - phase 1 scripted flow or phase 2 free-form instructions
 * @param contextAdaptations - context-mode adaptations (may be empty string)
 * @param traitOverlay - TARS-style trait calibration prompt from traitEngine
 */
export function buildTexture(
  patientSummary: string,
  registerFragment: string,
  phaseInstructions: string,
  contextAdaptations: string,
  traitOverlay: string,
): string {
  const parts = [
    patientSummary,
    '\n\nACTIVE REGISTER:\n' + registerFragment,
    traitOverlay,
  ];

  // Phase instructions and context adaptations come after the vertical layer
  // in the final assembly, but belong to texture conceptually.
  // We pack them into the texture string and let assembleNursePrompt place them.
  // The phaseInstructions and contextAdaptations are appended as trailing texture.
  parts.push(phaseInstructions);
  if (contextAdaptations) {
    parts.push(contextAdaptations);
  }

  return parts.join('');
}

/**
 * Assemble all three layers into a single prompt.
 *
 * Order matches the original buildNursePromptV3 return:
 *   [basePrompt, register, traitOverlay, behaviorRules, phaseInstructions, contextSection, formularySection].join('')
 *
 * Which maps to:
 *   texture-head (basePrompt + register + traitOverlay)
 *   + vertical (voiceRules + biomarkerCoverage + clinicalRules + formularySection)
 *   + foundation (honesty + boundaries)
 *   + texture-tail (phaseInstructions + contextSection)
 *
 * To reproduce the EXACT original order we split texture into head/tail sections.
 * However, for the extracted-layer API we keep it simpler: texture contains all
 * texture pieces, vertical contains voice/biomarker/clinical/formulary, and
 * foundation contains honesty/boundaries. The assembler replicates the original
 * concatenation order.
 */
export function assembleNursePrompt(
  foundation: string,
  vertical: string,
  texture: string,
): string {
  // Original order: basePrompt + register + traitOverlay + behaviorRules + phaseInstructions + contextSection + formularySection
  // texture = basePrompt + register + traitOverlay + phaseInstructions + contextSection
  // vertical = voiceRules + biomarkerCoverage + clinicalRules + formularySection
  // foundation = honesty + boundaries
  //
  // The original behaviorRules block is: voiceRules + biomarkerCoverage + clinicalRules + honesty/boundaries
  // So the vertical + foundation together form the old behaviorRules, plus formulary appended.
  //
  // Assembled in the same conceptual order: texture-head, vertical, foundation, texture-tail
  // But since we can't split texture here, we use the simpler ordering that keeps
  // the three layers distinct. When buildNursePromptV3 is wired to call these,
  // it can arrange the pieces to match the exact original order.
  return [texture, vertical, '\n\n' + foundation].join('\n\n');
}
